import { useFormikContext, withFormik } from "formik";
import { StatusCodes } from "http-status-codes";
import { useState } from "react";
import CookieConsent from "react-cookie-consent";
import { Link, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { OrButton } from "../shared_modules/ui/components/atoms/or-button/or-button";
import { OrCheckbox } from "../shared_modules/ui/components/atoms/or-checkbox/or-checkbox";
import { MdiEye, MdiEyeOff } from "../shared_modules/ui/components/atoms/or-icon";
import { OrInput } from "../shared_modules/ui/components/atoms/or-input/or-input";
import { OrToastContainer, showOrToast } from "../shared_modules/ui/components/atoms/or-toast/or-toast";
import { useUserRepository } from "../user/useUserRepository";
import { AuthShell } from "./auth-shell";
import "./auth-ui.css";
import { MarketingPreRegister, hasMarketingSearchParams } from "./marketing-pre-register";
import { useRegistrationVerification } from "./registration-verification-context";
import { SocialLogin } from "../social-auth/social-login";
import { useSocialMode } from "../social-auth/social-mode-context";
import { resolveLoginDestination } from "./login-destination";
import { SocialOnlyAccountNotice } from "./social-only-account-notice";

const ADMIN_ACCESS_ROLES = ["ROLE_ADMIN", "ROLE_SUPERADMIN", "ROLE_MARKETING", "ROLE_RRHH"];
const LOGIN_DESTINATIONS = {
  WORKER: process.env.WORKER,
  FRONT: process.env.FRONT,
  ADMIN: process.env.ADMIN,
  COMPANY: process.env.COMPANY,
};

function VerificationEmailResendIcon() {
  return (
    <svg
      aria-hidden="true"
      className="or-auth-verification-resend__icon"
      focusable="false"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="currentColor"
        d="M3 4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h10.5a6.5 6.5 0 0 1-.5-2H3V8l8 5l8-5v3a7 7 0 0 1 .5 0a6.5 6.5 0 0 1 1.5.18V6c0-1.1-.9-2-2-2zm0 2h16l-8 5zm16 6l-2.25 2.25L19 16.5V15a2.5 2.5 0 0 1 2.5 2.5c0 .4-.09.78-.26 1.12l1.09 1.09c.42-.63.67-1.39.67-2.21c0-2.21-1.79-4-4-4zm-3.33 3.29c-.42.63-.67 1.39-.67 2.21c0 2.21 1.79 4 4 4V23l2.25-2.25L19 18.5V20a2.5 2.5 0 0 1-2.5-2.5c0-.4.09-.78.26-1.12z"
      />
    </svg>
  );
}

export function Login() {
  const [searchParams] = useSearchParams();
  const { clearPendingVerificationEmail, pendingVerificationEmail } = useRegistrationVerification();
  const { login, loadCurrentUser, resendVerificationEmail } = useUserRepository();
  const { isSocialMode } = useSocialMode();
  const [socialOnlyAccount, setSocialOnlyAccount] = useState(false);

  if (isSocialMode) {
    return <SocialLogin />;
  }

  if (hasMarketingSearchParams(searchParams)) {
    return <MarketingPreRegister />;
  }

  let goto = searchParams.get("goto");

  if (goto) {
    localStorage.setItem("goto", goto);
    searchParams.delete("goto");
  } else if (localStorage.getItem("goto")) {
    goto = localStorage.getItem("goto");
  }

  const updatedParams = new URLSearchParams(searchParams.toString());
  window.history.replaceState({}, "", `/${updatedParams.toString()}`);

  const FormView = (props) => {
    const { setFieldValue } = useFormikContext();
    const { values, touched, errors, isSubmitting, handleChange, handleSubmit } = props;
    const [isResendingVerificationEmail, setIsResendingVerificationEmail] = useState(false);

    const handleResendVerificationEmail = async () => {
      if (!pendingVerificationEmail) {
        return;
      }

      setIsResendingVerificationEmail(true);

      try {
        const result = await resendVerificationEmail(pendingVerificationEmail);

        if (result) {
          showOrToast("Te hemos enviado un nuevo correo de verificación");
        } else {
          showOrToast("No hemos podido reenviar el correo de verificación", { type: "error" });
        }
      } catch (error) {
        showOrToast("No hemos podido reenviar el correo de verificación", { type: "error" });
      }

      setIsResendingVerificationEmail(false);
    };

    return (
      <form className="or-auth-form" onSubmit={handleSubmit}>
        <CookieConsent
          location="bottom"
          buttonText="Aceptar"
          cookieName="myAwesomeCookieName2"
          style={{ background: "rgb(28, 28, 28)", zIndex: "1500" }}
          buttonStyle={{ background: "#fcac04", color: "black", borderRadius: "3px", width: "80px" }}
          expires={150}
          enableDeclineButton={true}
          declineButtonText="Rechazar"
          declineButtonStyle={{ borderRadius: "3px", width: "80px" }}
        >
          Esta web utiliza cookies técnicas, de personalización y análisis, propias y de terceros, que tratan datos de
          conexión y/o del dispositivo para facilitarle la navegación y analizar estadísticas del uso de la web.{" "}
          <a
            className="cookielink"
            href="https://openreferences.com/politica-de-cookies"
            target="_blank"
            style={{ color: "#fcac04" }}
            rel="noreferrer"
          >
            Obtener más información.
          </a>
          .
        </CookieConsent>

        {pendingVerificationEmail ? (
          <div className="or-auth-verification-resend" role="group" aria-label="Reenvío de correo de verificación">
            <span className="or-auth-verification-resend__title">¿Te llegó el email?</span>
            <p className="or-auth-verification-resend__description">
              Para <b>activar tu cuenta</b> debes hacer clic en <strong>"Validar correo electrónico"</strong>.
              Si no encuentras el email, revisa <strong>spam</strong> o correo no deseado.
            </p>
            <div>
              <OrButton
                className="or-auth-verification-resend__button"
                disabled={isSubmitting}
                fullWidth
                isLoading={isResendingVerificationEmail}
                leftSlot={<VerificationEmailResendIcon />}
                onClick={handleResendVerificationEmail}
                size="md"
                type="button"
                variant="ghost"
              >
                Volver a enviar correo de verificación
              </OrButton>
            </div>
            <p className="or-auth-verification-resend__description">
              ¿Sigues con problemas? Escríbenos a <a href="mailto:contacto@openreferences.com">contacto@openreferences.com</a>
            </p>
          </div>
        ) : null}

        <div className="or-auth-form__group">
          <OrInput
            id="email"
            inputType="email"
            label="Email *"
            name="email"
            onChange={handleChange}
            placeholder="tu@email.com"
            value={values.email}
            hasError={touched.email && Boolean(errors.email)}
            errorText={touched.email ? errors.email : ""}
          />
        </div>

        <div className="or-auth-form__group">
          <OrInput
            id="password"
            inputType={values.showPassword ? "text" : "password"}
            label="Contraseña *"
            name="password"
            onChange={handleChange}
            placeholder="********************"
            rightSlot={
              <button
                className="or-auth-password-toggle"
                id="passwordicon"
                type="button"
                aria-label="toggle password visibility"
                onClick={() => setFieldValue("showPassword", !values.showPassword)}
              >
                {values.showPassword ? <MdiEyeOff /> : <MdiEye />}
              </button>
            }
            value={values.password}
            hasError={touched.password && Boolean(errors.password)}
            errorText={touched.password ? errors.password : ""}
          />
        </div>

        <div className="or-auth-form__group or-auth-form__group--terms">
          <div className="or-auth-form__links-row">
            <OrCheckbox className="or-auth-checkbox" id="remember-me" label="Recuérdame" />
            <Link className="or-auth-form__helper-link" to="/retrieve">
              Recuperar contraseña
            </Link>
          </div>
        </div>

        <div className="or-auth-form__group">
          <OrButton id="login-btn" type="submit" fullWidth isLoading={isSubmitting} size="lg" variant="primary">
            Iniciar sesión
          </OrButton>
        </div>
      </form>
    );
  };

  const validationsForm = {
    email: yup.string().email("Escribe un email válido").required("El email es requerido"),
    password: yup
      .string()
      .min(8, "La contraseña es demasiado corta. debe tener un mínimo de 8 caracteres.")
      .required("La password es requerida"),
  };

  const Form = withFormik({
    mapPropsToValues: ({ email, password }) => {
      return {
        email: email || "",
        password: password || "",
      };
    },

    enableReinitialize: true,
    validationSchema: yup.object().shape(validationsForm),
    handleSubmit: async (values) => {
      try {
        let result = await login({ username: values.email, password: values.password });

        if (result.status === StatusCodes.OK) {
          let user = await loadCurrentUser(result.data.token);
          clearPendingVerificationEmail();

          const destination = resolveLoginDestination(user, result.data.token, goto, LOGIN_DESTINATIONS, ADMIN_ACCESS_ROLES);
          if (!destination) {
            setSocialOnlyAccount(true);
            return;
          }
          if (goto) {
            localStorage.removeItem("goto");
          }
          window.location.href = destination;
        }
      } catch (error) {
        showOrToast("Usuario o contraseña incorrecta", { type: "error" });
      }
    },
  })(FormView);

  return (
    <AuthShell
      title="Bienvenido de nuevo"
      subtitle="Ingresa tus credenciales para acceder a tu cuenta"
      footer={
        <>
          <p className="or-auth-form__hint">
            ¿No tienes cuenta? <Link to="/register">Regístrate</Link>
          </p>
          <a href="https://openreferences.com/aviso-legal/" target="_blank" rel="noreferrer">
            Términos y Condiciones
          </a>
        </>
      }
    >
      <OrToastContainer />
      <Form email={pendingVerificationEmail} />
      {socialOnlyAccount ? <SocialOnlyAccountNotice /> : null}
    </AuthShell>
  );
}
